<template>
  <div class="mall-address-register">
    <div class="page">
      <MallHeader @back-click="goBack">
        <div slot="right"></div>
      </MallHeader>

      <div class="mall-address-register__content">
        <div class="mall-address-register__top">
          <!-- Figma: 56px 이미지를 52px 영역에 맞춰 가장자리를 2px씩 자름 -->
          <div class="mall-address-register__icon">
            <Mall3dIcon :src="homeIcon" :size="56" :screenOpacity="0.4" />
          </div>
          <div class="mall-address-register__title">
            <span class="mall-address-register__heading">배송지 입력</span>
            <span class="mall-address-register__desc">정확한 주소를 입력하면<br />안전하게 배송돼요.</span>
          </div>
        </div>

        <div class="mall-address-register__form">
          <div class="mall-address-register__address">
            <div class="mall-address-register__address-row">
              <MallTextField
                class="mall-address-register__address-input"
                :value="form.address"
                :label="'주소'"
                :required="true"
                :readonly="true"
                :placeholder="'건물, 지번 또는 도로명 검색'"
                :invalid="!!errors.address"
                @click.native="openPostcode"
              />
              <MallButton
                class="mall-address-register__search-button"
                :label="'주소 찾기'"
                :type="'secondary'"
                :size="'md'"
                @click="openPostcode"
              />
            </div>
            <span v-if="errors.address" class="mall-address-register__error">{{ errors.address }}</span>
            <MallTextField
              v-model="form.detailAddress"
              :placeholder="'상세 주소를 알려주세요'"
              :error="errors.detailAddress"
            />
          </div>
          <MallTextField
            v-model="form.recipient"
            :label="'받는 분'"
            :required="true"
            :placeholder="'받는 분 성함을 알려주세요'"
            :error="errors.recipient"
          />
          <MallTextField
            v-model="form.message"
            :label="'배송 메시지'"
            :required="true"
            :placeholder="'배송 시 요청 사항을 입력해 주세요'"
            :error="errors.message"
          />
        </div>
      </div>

      <MallBottomBar :buttons="buttons" @click="save" />

      <!-- 카카오 주소 검색 -->
      <MallPostcodeLayer
        v-if="isPostcodeOpen"
        @select="onAddressSelect"
        @close="isPostcodeOpen = false"
        @error="onPostcodeError"
      />

      <!-- 입력 중에 나가려고 할 때 -->
      <MallNoticeSheet
        v-if="isLeaveSheet"
        :title="'주소 등록을 취소할까요?'"
        :messageLines="['지금 나가면 입력한 내용이 저장되지 않아요.']"
        :cancelLabel="'나가기'"
        :confirmLabel="'계속 등록하기'"
        :dim="'normal'"
        @cancel="leave"
        @close="stay"
        @confirm="stay"
      />

      <MallToast
        v-if="toast"
        :key="toast.key"
        :title="toast.title"
        :message="toast.message"
        @close="toast = null"
      />
    </div>
  </div>
</template>

<script>
import Constant from '@/constant'
import mall from '@/utilities/mall.js'
import Mall3dIcon from '@/components/new/atoms/Mall3dIcon.vue'
import MallButton from '@/components/new/atoms/MallButton.vue'
import MallBottomBar from '@/components/new/molecules/MallBottomBar.vue'
import MallHeader from '@/components/new/molecules/MallHeader.vue'
import MallTextField from '@/components/new/molecules/MallTextField.vue'
import MallToast from '@/components/new/molecules/MallToast.vue'
import MallNoticeSheet from '@/components/new/organisms/MallNoticeSheet.vue'
import MallPostcodeLayer from '@/components/new/organisms/MallPostcodeLayer.vue'

var EMPTY_FORM = {
  address: '',
  zipCode: '',
  detailAddress: '',
  recipient: '',
  message: '',
}

// 배송지 등록·수정 (기존 /address 화면의 배송지 등록과 별개)
export default {
  name: 'MallAddressRegister',
  components: {
    Mall3dIcon,
    MallButton,
    MallBottomBar,
    MallHeader,
    MallTextField,
    MallToast,
    MallNoticeSheet,
    MallPostcodeLayer,
  },
  data() {
    return {
      homeIcon: require('@/assets/mall/img_home_3d.png'),
      profileId: Number(this.$route.query.profileId) || null,
      token: null,
      form: Object.assign({}, EMPTY_FORM),
      // 수정이면 불러온 배송지 (입력이 바뀌었는지 비교용)
      initialForm: Object.assign({}, EMPTY_FORM),
      isEdit: false,
      // 입력이 덜 된 상태로 등록 버튼을 누르면 빈 칸까지 모두 오류 노출
      isSubmitted: false,
      isRequesting: false,
      isPostcodeOpen: false,
      // 나가기 확인 시트 / 보류 중인 화면 이동 (beforeRouteLeave의 next)
      isLeaveSheet: false,
      pendingLeave: null,
      // 저장을 마쳤거나 로그인으로 보낼 때는 확인 없이 이동
      canLeave: false,
      toast: null,
    }
  },
  computed: {
    isDirty() {
      return Object.keys(EMPTY_FORM).some((key) => this.form[key] !== this.initialForm[key])
    },
    // 칸별 오류 문구 (문제가 없으면 '')
    fieldErrors() {
      var form = this.form
      return {
        address: form.address ? '' : '주소 찾기로 주소를 검색해 주세요.',
        detailAddress: form.detailAddress.trim() ? '' : '상세 주소를 입력해 주세요.',
        recipient: form.recipient.trim() ? '' : '받는 분 성함을 입력해 주세요.',
        message: form.message.trim() ? '' : '배송 메시지를 입력해 주세요.',
      }
    },
    // 화면에 보이는 오류 - 모두 필수 입력이라 등록 버튼을 누른 뒤에만 노출
    errors() {
      var errors = {}
      Object.keys(this.fieldErrors).forEach((key) => {
        errors[key] = this.isSubmitted ? this.fieldErrors[key] : ''
      })
      return errors
    },
    isValid() {
      var fieldErrors = this.fieldErrors
      return Object.keys(fieldErrors).every((key) => fieldErrors[key] === '')
    },
    // 입력이 덜 됐어도 누를 수 있게 두고(비활성 모양) 누르면 이유를 안내
    buttons() {
      var label = this.isEdit ? '배송지 수정하기' : '배송지 등록하기'
      return [{ key: 'save', label: label, type: 'primary', dimmed: !this.isValid, disabled: this.isRequesting }]
    },
  },
  mounted() {
    ChannelIO('shutdown')
    this.token = mall.getAccessToken()
    if (!this.token) {
      this.goLogin()
      return
    }
    // 등록된 배송지가 있으면 채워서 수정
    mall.fetchProfile(this.$store, this.profileId, this.token)
      .then((item) => {
        var profile = item ? item.profile : null
        if (!profile || !profile.address || this.isDirty) return
        var saved = {
          address: profile.address,
          zipCode: profile.zip_code || '',
          detailAddress: profile.detail_address || '',
          recipient: profile.address_nickname || '',
          message: profile.address_description || '',
        }
        this.form = Object.assign({}, saved)
        this.initialForm = Object.assign({}, saved)
        this.isEdit = true
      })
      .catch((err) => {
        if (mall.isAuthError(err)) this.goLogin()
      })
  },
  // 주소 검색 중이면 검색만 닫고, 입력한 내용이 있으면 확인 시트 (헤더·기기 뒤로가기 모두)
  beforeRouteLeave(to, from, next) {
    if (this.isPostcodeOpen) {
      this.isPostcodeOpen = false
      next(false)
      return
    }
    if (this.canLeave || !this.isDirty) {
      next()
      return
    }
    if (this.pendingLeave) this.pendingLeave(false)
    this.pendingLeave = next
    this.isLeaveSheet = true
  },
  methods: {
    // 헤더 뒤로가기는 화면 이동 전에 확인 시트를 띄움 (기기 뒤로가기는 beforeRouteLeave에서 처리)
    goBack() {
      if (!this.canLeave && this.isDirty) {
        this.isLeaveSheet = true
        return
      }
      this.moveBack()
    },
    // 배송지 관리로 (관리 화면의 returnTo 유지) - 저장 후에도 같은 화면으로
    moveBack() {
      mall.goBack(this.$router, this.$route, { path: '/mall/address', query: { profileId: this.profileId } })
    },
    goLogin() {
      this.canLeave = true
      mall.goLogin(this.$router)
    },
    leave() {
      var next = this.pendingLeave
      this.pendingLeave = null
      this.isLeaveSheet = false
      this.canLeave = true
      if (next) {
        next()
      } else {
        this.moveBack()
      }
    },
    stay() {
      var next = this.pendingLeave
      this.pendingLeave = null
      this.isLeaveSheet = false
      if (next) next(false)
    },
    openPostcode() {
      this.isPostcodeOpen = true
    },
    onAddressSelect(selected) {
      this.form.address = selected.address
      this.form.zipCode = selected.zipCode
      this.isPostcodeOpen = false
    },
    onPostcodeError() {
      this.isPostcodeOpen = false
      this.toast = { key: Date.now(), title: '주소 검색을 열지 못했어요.', message: '잠시 후 다시 시도해 주세요.' }
    },
    // 입력이 덜 된 채로 등록을 누르면 빈 칸을 모두 표시하고 첫 칸으로 스크롤
    showInvalidReason() {
      this.isSubmitted = true
      this.$nextTick(() => {
        var target = this.$el.querySelector('.mall-text-field__input--error')
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    save() {
      if (this.isRequesting) return
      if (!this.isValid) {
        this.showInvalidReason()
        return
      }
      var form = this.form
      var address = {
        address: form.address,
        detail_address: form.detailAddress.trim(),
        zip_code: form.zipCode,
        // 받는 분은 address_nickname 에 저장 (기존 배송지 등록과 동일)
        address_nickname: form.recipient.trim(),
        address_description: form.message.trim(),
      }
      this.isRequesting = true
      this.$store.dispatch(Constant.UPDATE_PROFILE_ADDRESS, { address: address, profile_id: this.profileId, token: this.token })
        .then(() => {
          this.canLeave = true
          this.moveBack()
        })
        .catch((err) => {
          this.isRequesting = false
          if (mall.isAuthError(err)) {
            this.goLogin()
            return
          }
          this.toast = { key: Date.now(), title: '배송지를 저장하지 못했어요.', message: mall.errorMessage(err) }
        })
    },
  },
}
</script>

<style scoped>
  .mall-address-register {
    display: flex;
    width: 100%;
    min-height: 100vh;
    justify-content: center;
    align-items: flex-start;
  }
  /* 하단 고정 버튼 영역(90px)에 내용이 가려지지 않도록 */
  .page {
    width: 100%;
    min-height: 100vh;
    padding-bottom: 90px;
    background: #FFF;
    box-sizing: border-box;
  }
  .mall-address-register__content {
    display: flex;
    padding: 4px 16px 32px 16px;
    flex-direction: column;
    gap: 48px;
  }
  .mall-address-register__top {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .mall-address-register__icon {
    width: 52px;
    height: 52px;
    overflow: hidden;
  }
  .mall-address-register__icon .mall-3d-icon {
    margin: -2px 0 0 -2px;
  }
  .mall-address-register__title {
    display: flex;
    padding-left: 4px;
    flex-direction: column;
    gap: 4px;
  }
  .mall-address-register__heading {
    color: var(--color-text-main);
    font-family: Pretendard;
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-semibold);
    line-height: var(--line-height-tight);
  }
  .mall-address-register__desc {
    color: #6B7684;
    font-family: Pretendard;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    line-height: var(--line-height-normal);
  }
  .mall-address-register__form {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
  .mall-address-register__address {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .mall-address-register__address-row {
    display: flex;
    align-items: flex-end;
    gap: 4px;
  }
  .mall-address-register__address-input {
    flex: 1;
    min-width: 0;
  }
  .mall-address-register__address-row .mall-address-register__search-button {
    flex: none;
  }
  /* MallTextField 오류 문구와 같은 모양 (주소 칸은 버튼과 한 줄이라 아래에 따로 노출) */
  .mall-address-register__error {
    margin-top: -4px;
    padding: 0 4px;
    color: #F04452;
    font-family: Pretendard;
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-medium);
    line-height: 17px;
  }
  @media (min-width: 600px) {
    .page {
      width: 600px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/page/new/MallAddressRegister.vue